import { useCallback, useState } from 'react';
import { THEME_LABELS } from '@/lib/company-qualification';
import { type RestitutionScope, type RestitutionTarget, writeRestitution } from '@/lib/company-restitution';
import type { RestitutionWriting } from '@/components/profile/restitution/RestitutionView';
import type { CompanyQualificationSheet } from '@shared/types/company-qualification';

const partLabel = (target: RestitutionTarget): string => (target === 'brief' ? 'la synthèse' : THEME_LABELS[target].label);

/** Writes the restitution and follows its parts as the server announces them. */
export const useRestitutionWriting = (onWritten: (sheet: CompanyQualificationSheet, version: number) => void) => {
  const [writing, setWriting] = useState<RestitutionWriting | null>(null);
  const write = useCallback(async (scope: RestitutionScope) => {
    setWriting({ running: true, targets: [], done: {}, error: null });
    try {
      const outcome = await writeRestitution(scope, {
        onTargets: (targets) => setWriting((current) => current && { ...current, targets }),
        onPart: (target, ok) => setWriting((current) => current && { ...current, done: { ...current.done, [target]: ok } }),
      });
      onWritten(outcome.sheet, outcome.version);
      const failed = outcome.failed.map(partLabel);
      setWriting((current) => current && {
        ...current, running: false,
        error: failed.length ? `Pas encore rédigé : ${failed.join(', ')}. Relancez la mise à jour.` : null,
      });
    } catch (cause) {
      const error = cause instanceof Error ? cause.message : 'La restitution n’a pas pu être rédigée.';
      setWriting((current) => ({ targets: [], done: {}, ...current, running: false, error }));
    }
  }, [onWritten]);
  return { writing, write };
};
